"use client";

import { c as _c } from "react/compiler-runtime";
import { CompactModeProvider } from "@/contexts/CompactModeContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Transition } from "@headlessui/react";
import { markDropOpenReady } from "@/utils/monitoring/dropOpenTiming";
import { ArrowLeftIcon, ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { SingleWaveDropChat } from "./SingleWaveDropChat";
interface SingleWaveDropWrapperProps {
  readonly drop: ApiDrop;
  readonly wave: ApiWave;
  readonly children: React.ReactNode;
  readonly onClose: () => void;
}
export const SingleWaveDropWrapper: React.FC<SingleWaveDropWrapperProps> = t0 => {
  const $ = _c(51);
  const {
    drop,
    wave,
    children,
    onClose
  } = t0;
  const [isChatOpen, setIsChatOpen] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width: 1023px)");
  const readyKeyRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsChatOpen(_temp);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const toggleChat = t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const browserWindow = globalThis.window;
      if (browserWindow === undefined) {
        return;
      }
      const handleClose = () => setIsChatOpen(false);
      browserWindow.addEventListener("single-drop:close-chat", handleClose);
      return () => browserWindow.removeEventListener("single-drop:close-chat", handleClose);
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== isChatOpen || $[4] !== isSmallScreen) {
    t4 = () => {
      if (!isChatOpen || !isSmallScreen) {
        return;
      }
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    };
    t5 = [isChatOpen, isSmallScreen];
    $[3] = isChatOpen;
    $[4] = isSmallScreen;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[7] !== drop.id || $[8] !== wave.id) {
    t6 = () => {
      const readyKey = `${drop.id}:${wave.id}`;
      if (readyKeyRef.current === readyKey) {
        return;
      }
      readyKeyRef.current = readyKey;
      markDropOpenReady({
        dropId: drop.id,
        waveId: wave.id
      });
    };
    t7 = [drop.id, wave.id];
    $[7] = drop.id;
    $[8] = wave.id;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ArrowLeftIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 sm:tw-h-4 sm:tw-w-4" />;
    t9 = <span className="tw-hidden tw-text-sm tw-font-semibold sm:tw-inline">Close</span>;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  let t10;
  if ($[13] !== onClose) {
    t10 = <div className="tw-flex tw-items-center tw-gap-6"><button type="button" aria-label="Close panel" onClick={onClose} className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-950 tw-text-iron-300 tw-transition-all desktop-hover:hover:tw-text-white sm:tw-h-auto sm:tw-w-auto sm:tw-rounded-lg sm:tw-border-0 sm:tw-bg-transparent sm:tw-px-3 sm:tw-py-2">{t8}{t9}</button></div>;
    $[13] = onClose;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const t11 = isChatOpen ? "Hide chat" : "Show chat";
  const t12 = `tw-flex tw-h-11 tw-w-11 tw-flex-shrink-0 tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-solid tw-px-3 tw-text-sm tw-font-medium tw-backdrop-blur-md tw-transition-all sm:tw-h-auto sm:tw-w-auto sm:tw-rounded-lg sm:tw-py-2 ${isChatOpen ? "tw-border-iron-700 tw-bg-iron-800 tw-text-iron-100 desktop-hover:hover:tw-bg-iron-700" : "tw-border-iron-700 tw-bg-iron-950 tw-text-iron-300 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-iron-200"}`;
  let t13;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ChatBubbleLeftRightIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 sm:tw-h-4 sm:tw-w-4" />;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  const t14 = isChatOpen ? "Hide" : "Show";
  let t15;
  if ($[16] !== t14) {
    t15 = <span className="tw-hidden sm:tw-inline">{t14} Chat</span>;
    $[16] = t14;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== t11 || $[19] !== t12 || $[20] !== t15) {
    t16 = <button onClick={toggleChat} aria-label={t11} className={t12}>{t13}{t15}</button>;
    $[18] = t11;
    $[19] = t12;
    $[20] = t15;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  let t17;
  if ($[22] !== t10 || $[23] !== t16) {
    t17 = <header className="tw-absolute tw-left-0 tw-right-3 tw-top-0 tw-z-20 tw-bg-iron-950/55 tw-bg-gradient-to-b tw-from-iron-950/75 tw-via-iron-950/55 tw-to-iron-950/10 tw-px-2 tw-pb-2 tw-pt-[calc(env(safe-area-inset-top,0px)+0.5rem)] tw-backdrop-blur-sm @[640px]:tw-px-8 lg:tw-px-8"><div className="tw-flex tw-w-full tw-items-center tw-justify-between">{t10}{t16}</div></header>;
    $[22] = t10;
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="tw-h-[calc(env(safe-area-inset-top,0px)+4rem)] tw-shrink-0" aria-hidden="true" />;
    $[25] = t18;
  } else {
    t18 = $[25];
  }
  let t19;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <div className="tw-h-[env(safe-area-inset-bottom,0px)] tw-shrink-0" aria-hidden="true" />;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== children) {
    t20 = <div className="tw-[scrollbar-gutter:stable] tw-h-full tw-overflow-y-auto tw-overscroll-contain tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300">{t18}{children}{t19}</div>;
    $[27] = children;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  let t21;
  if ($[29] !== t17 || $[30] !== t20) {
    t21 = <div className="@container tw-relative tw-h-full tw-flex-1 tw-overflow-hidden">{t17}{t20}</div>;
    $[29] = t17;
    $[30] = t20;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  const t22 = `tw-hidden tw-h-full tw-flex-col tw-bg-black tw-transition-all tw-duration-300 tw-ease-in-out lg:tw-flex ${isChatOpen ? "tw-w-[400px] tw-border-y-0 tw-border-l tw-border-r-0 tw-border-solid tw-border-white/10" : "tw-w-0 tw-overflow-hidden tw-border-none"} `;
  let t23;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-z-50 tw-h-12 tw-bg-gradient-to-b tw-from-black tw-to-transparent" />;
    $[32] = t23;
  } else {
    t23 = $[32];
  }
  let t24;
  if ($[33] !== drop || $[34] !== wave) {
    t24 = <div className="@container tw-relative tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">{t23}<SingleWaveDropChat key={drop.id} wave={wave} drop={drop} /></div>;
    $[33] = drop;
    $[34] = wave;
    $[35] = t24;
  } else {
    t24 = $[35];
  }
  let t25;
  if ($[36] !== t22 || $[37] !== t24) {
    t25 = <CompactModeProvider compact={true}><div className={t22}>{t24}</div></CompactModeProvider>;
    $[36] = t22;
    $[37] = t24;
    $[38] = t25;
  } else {
    t25 = $[38];
  }
  let t26;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Transition.Child as={Fragment} enter="tw-duration-150 tw-ease-out" enterFrom="tw-opacity-0" enterTo="tw-opacity-100" leave="tw-duration-120 tw-ease-in" leaveFrom="tw-opacity-100" leaveTo="tw-opacity-0"><div className="tw-absolute tw-inset-0 tw-bg-black/60" onClick={toggleChat} /></Transition.Child>;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="tw-relative tw-z-10 tw-flex tw-flex-none tw-items-center tw-px-2 tw-pb-2 tw-pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] lg:tw-px-8"><button onClick={toggleChat} className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-white/70 tw-transition-colors desktop-hover:hover:tw-text-white"><ArrowLeftIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" /><span className="tw-text-sm tw-font-semibold">Close</span></button></div>;
    $[40] = t27;
  } else {
    t27 = $[40];
  }
  let t28;
  if ($[41] !== drop || $[42] !== wave) {
    t28 = <div className="tw-fixed tw-inset-y-0 tw-left-[var(--left-rail,0px)] tw-right-0 tw-z-[90] tw-h-[100dvh] tw-max-h-[100dvh] tw-overflow-hidden tw-overscroll-none lg:tw-hidden">{t26}<Transition.Child as={Fragment} enter="tw-duration-220 tw-transform tw-transition tw-ease-out" enterFrom="tw-translate-x-full" enterTo="tw-translate-x-0" leave="tw-duration-180 tw-transform tw-transition tw-ease-in" leaveFrom="tw-translate-x-0" leaveTo="tw-translate-x-full"><div className="@container tw-absolute tw-inset-0 tw-z-[100] tw-flex tw-h-full tw-min-h-0 tw-transform-gpu tw-flex-col tw-overflow-hidden tw-overscroll-none tw-bg-iron-950 tw-will-change-transform lg:tw-hidden">{t27}<div className="@container tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden"><SingleWaveDropChat key={drop.id} wave={wave} drop={drop} /></div></div></Transition.Child></div>;
    $[41] = drop;
    $[42] = wave;
    $[43] = t28;
  } else {
    t28 = $[43];
  }
  let t29;
  if ($[44] !== isChatOpen || $[45] !== t28) {
    t29 = <CompactModeProvider compact={true}><Transition.Root show={isChatOpen} as={Fragment}>{t28}</Transition.Root></CompactModeProvider>;
    $[44] = isChatOpen;
    $[45] = t28;
    $[46] = t29;
  } else {
    t29 = $[46];
  }
  let t30;
  if ($[47] !== t21 || $[48] !== t25 || $[49] !== t29) {
    t30 = <div className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-overscroll-none tw-bg-iron-950 lg:tw-flex-row">{t21}{t25}{t29}</div>;
    $[47] = t21;
    $[48] = t25;
    $[49] = t29;
    $[50] = t30;
  } else {
    t30 = $[50];
  }
  return t30;
};
function _temp(prev) {
  return !prev;
}